import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck, ArrowUpRight, Megaphone, Mail, LogIn, UserCircle, LogOut } from "lucide-react";
import { NAV, FOOTER } from "@/constants/testIds";
import { IMG } from "@/constants/images";
import { BuyMeACoffeeButton } from "@/components/BuyMeACoffeeButton";
import { AuthModal } from "@/components/AuthModal";
import { useAuth } from "@/context/AuthContext";

const PRIMARY_NAV = [
  { to: "/", label: "Home", testid: NAV.home, end: true },
  { to: "/about", label: "About", testid: NAV.about },
  { to: "/contact", label: "Contact", testid: NAV.contact },
];

// The 5 Pillars of OGO Excellence — surfaced as the secondary nav row on
// desktop and as their own labeled block in the mobile drawer. Each item
// deep-links to the canonical page for that pillar.
const PILLAR_NAV = [
  { to: "/mobile-notary", label: "Mobile & RON", testid: NAV.mobile },
  { to: "/signing-agent", label: "Real Estate & Signings", testid: NAV.signingAgent },
  { to: "/apostille", label: "Gov & Apostille", testid: "nav-apostille" },
  { to: "/wedding-services", label: "New Beginnings", testid: "nav-wedding" },
  { to: "/elder-care", label: "Elder Care", testid: "nav-elder-care" },
];

const RESOURCE_NAV = [
  { to: "/pricing", label: "Pricing", testid: NAV.pricing },
  { to: "/faq", label: "FAQ", testid: NAV.faq },
  { to: "/resources", label: "Resources", testid: "nav-resources", gold: true },
  { to: "/blog", label: "Blog & Newsletter", testid: "nav-blog", gold: true },
  { to: "/apps", label: "Apps", testid: "nav-apps" },
  { to: "/gear", label: "Field-Tested Gear", testid: "nav-gear", gold: true },
];

// Legacy alias — the FAQ SEO card + a couple of screenshot tests still hunt
// for individual service routes, so keep this "everything the user could want"
// list around for the footer render.
const SERVICE_NAV = [...PILLAR_NAV, ...RESOURCE_NAV];

const ALL_NAV = [...PRIMARY_NAV, ...SERVICE_NAV];

function Header() {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#142339]/85 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* ROW 1: Brand + Primary nav + CTAs */}
        <div className="flex items-center justify-between h-24 lg:h-28 gap-6">
          <Link
            to="/"
            data-testid={NAV.brand}
            className="group flex items-center gap-3 shrink-0"
          >
            <img
              src={IMG.brandLogo}
              alt="OGO Notary owl emblem"
              className="h-12 sm:h-14 lg:h-16 w-auto"
            />
            <div className="leading-tight whitespace-nowrap">
              <div className="font-serif-display text-base lg:text-lg text-white tracking-tight">
                OGO Notary & Signing Services
              </div>
              <div className="hidden xl:block text-[10px] uppercase tracking-[0.28em] text-slate-400">
                An OldGuyOutside, LLC Company
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {PRIMARY_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.18em] font-medium transition-colors ${
                    isActive
                      ? "text-[#D4AF37]"
                      : "text-slate-300 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <MemberAuthControl variant="desktop" />
            <Link
              to="/contact"
              data-testid="header-book-btn"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#142339] px-4 py-3 text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-[#FDE047] transition-colors"
            >
              Book Appointment
            </Link>
            <Link
              to="/vendor"
              data-testid="vendor-cta-header"
              className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-4 py-3 text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-[#D4AF37]/10 transition-colors"
              title="For title, escrow & loan officers"
            >
              For Vendors
            </Link>
          </div>

          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-white"
            aria-label="Toggle navigation"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* ROW 2: 5 Pillars strip (desktop only) */}
        <nav
          data-testid="nav-pillars-strip"
          className="hidden lg:block border-t border-white/10"
        >
          <div className="flex flex-wrap items-center justify-center gap-x-5 xl:gap-x-7 gap-y-1 py-3">
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]/80 font-semibold pr-1">
              5 Pillars ·
            </span>
            {PILLAR_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors ${
                    isActive
                      ? "text-[#D4AF37]"
                      : "text-slate-100 hover:text-[#D4AF37]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* ROW 3: Resources & More strip (desktop only) */}
        <nav
          data-testid="nav-resources-strip"
          className="hidden lg:block border-t border-white/10 bg-white/[0.02]"
        >
          <div className="flex flex-wrap items-center justify-center gap-x-6 xl:gap-x-8 gap-y-1 py-3">
            {RESOURCE_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) => {
                  if (item.gold) {
                    return `text-[12px] uppercase tracking-[0.22em] font-bold transition-colors ${
                      isActive
                        ? "text-[#FDE047]"
                        : "text-[#D4AF37] hover:text-[#FDE047]"
                    }`;
                  }
                  return `text-[12px] uppercase tracking-[0.22em] font-semibold transition-colors ${
                    isActive
                      ? "text-[#D4AF37]"
                      : "text-slate-100 hover:text-[#D4AF37]"
                  }`;
                }}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>

        {/* MOBILE PANEL */}
        {open && (
          <div
            data-testid={NAV.mobilePanel}
            className="lg:hidden pb-6 border-t border-white/10 max-h-[calc(100dvh-6rem)] overflow-y-auto overscroll-contain"
          >
            <div className="flex flex-col py-4 gap-1">
              <div className="px-3 pt-1 pb-2 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                Main
              </div>
              {PRIMARY_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`${item.testid}-mobile`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-sm uppercase tracking-[0.18em] border-l-2 ${
                      isActive
                        ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                        : "border-transparent text-slate-300 hover:text-white hover:border-white/30"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}

              <div className="px-3 pt-5 pb-2 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                Our 5 Pillars
              </div>
              {PILLAR_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`${item.testid}-mobile`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-sm uppercase tracking-[0.18em] border-l-2 ${
                      isActive
                        ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                        : "border-transparent text-slate-300 hover:text-white hover:border-white/30"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}

              <div className="px-3 pt-5 pb-2 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                Resources & More
              </div>
              {RESOURCE_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`${item.testid}-mobile`}
                  className={({ isActive }) => {
                    if (item.gold) {
                      return `px-3 py-3 text-sm uppercase tracking-[0.18em] border-l-2 font-bold ${
                        isActive
                          ? "border-[#FDE047] text-[#FDE047] bg-[#D4AF37]/10"
                          : "border-[#D4AF37]/60 text-[#D4AF37] hover:text-[#FDE047] hover:border-[#FDE047]"
                      }`;
                    }
                    return `px-3 py-3 text-sm uppercase tracking-[0.18em] border-l-2 ${
                      isActive
                        ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                        : "border-transparent text-slate-200 hover:text-white hover:border-white/30"
                    }`;
                  }}
                >
                  {item.label}
                </NavLink>
              ))}

              <Link
                to="/contact"
                data-testid="header-book-btn-mobile"
                className="mt-5 inline-flex justify-center items-center gap-2 bg-[#D4AF37] text-[#142339] px-5 py-4 text-xs uppercase tracking-[0.22em] font-bold"
              >
                Book Appointment <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/vendor"
                data-testid="vendor-cta-header-mobile"
                className="mt-2 inline-flex justify-center items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-5 py-4 text-xs uppercase tracking-[0.22em] font-bold"
              >
                For Vendors — Title / Escrow / Loan
              </Link>
              <MemberAuthControl variant="mobile" />
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function MemberAuthControl({ variant }) {
  const { isAuthed, user, logout } = useAuth();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [initialTab, setInitialTab] = React.useState("login");
  const [menuOpen, setMenuOpen] = React.useState(false);

  const openLogin = () => {
    setInitialTab("login");
    setModalOpen(true);
  };

  const firstName = (user?.name || "").trim().split(/\s+/)[0] || "Member";

  if (variant === "mobile") {
    return (
      <>
        {isAuthed ? (
          <div className="mt-4 pt-4 border-t border-white/10 flex flex-col gap-2">
            <Link
              to="/community"
              data-testid="nav-community-mobile"
              className="inline-flex justify-center items-center gap-2 bg-[#0A1020] border border-[#D4AF37] text-[#D4AF37] px-5 py-4 text-xs uppercase tracking-[0.22em] font-bold"
            >
              <UserCircle className="w-4 h-4" />
              Community Hub · {firstName}
            </Link>
            <button
              type="button"
              onClick={logout}
              data-testid="nav-logout-mobile"
              className="inline-flex justify-center items-center gap-2 border border-white/15 text-slate-300 px-5 py-3 text-[10px] uppercase tracking-[0.22em] font-bold"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign Out
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={openLogin}
            data-testid="nav-login-mobile"
            className="mt-4 inline-flex justify-center items-center gap-2 border border-white/20 text-white px-5 py-4 text-xs uppercase tracking-[0.22em] font-bold"
          >
            <LogIn className="w-4 h-4" /> Login / Join Community
          </button>
        )}
        <AuthModal open={modalOpen} onClose={() => setModalOpen(false)} initialTab={initialTab} />
      </>
    );
  }

  return (
    <>
      {isAuthed ? (
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="nav-user-menu-btn"
            className="inline-flex items-center gap-2 border border-white/15 text-white px-3 py-3 text-[10px] uppercase tracking-[0.22em] font-bold hover:border-[#D4AF37]/60"
          >
            <UserCircle className="w-4 h-4 text-[#D4AF37]" />
            <span>Welcome, {firstName}</span>
          </button>
          {menuOpen && (
            <div
              data-testid="nav-user-menu"
              className="absolute right-0 mt-2 w-52 border border-white/10 bg-[#0B1120] shadow-2xl z-50"
              onMouseLeave={() => setMenuOpen(false)}
            >
              <Link
                to="/community"
                onClick={() => setMenuOpen(false)}
                data-testid="nav-community-link"
                className="block px-4 py-3 text-[11px] uppercase tracking-[0.22em] text-slate-200 hover:bg-[#142339] hover:text-white"
              >
                My Community Hub
              </Link>
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
                data-testid="nav-logout-btn"
                className="w-full text-left px-4 py-3 text-[11px] uppercase tracking-[0.22em] text-slate-300 hover:bg-[#142339] hover:text-[#D4AF37] flex items-center gap-2"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign Out
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={openLogin}
          data-testid="nav-login-btn"
          className="inline-flex items-center gap-2 border border-white/20 text-white px-3 py-3 text-[10px] uppercase tracking-[0.22em] font-bold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
        >
          <LogIn className="w-4 h-4" />
          Login / Join
        </button>
      )}
      <AuthModal open={modalOpen} onClose={() => setModalOpen(false)} initialTab={initialTab} />
    </>
  );
}

function FooterAuthLink() {
  const { isAuthed } = useAuth();
  const [open, setOpen] = React.useState(false);
  if (isAuthed) {
    return (
      <Link
        to="/community"
        data-testid="footer-community-link"
        className="text-slate-400 hover:text-[#D4AF37]"
      >
        My Community Hub
      </Link>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="footer-login-btn"
        className="text-slate-400 hover:text-[#D4AF37] uppercase tracking-[0.22em]"
      >
        Login / Join
      </button>
      <AuthModal open={open} onClose={() => setOpen(false)} initialTab="login" />
    </>
  );
}

function Footer() {
  return (
    <footer
      data-testid={FOOTER.root}
      className="bg-[#0F1A2E] border-t border-white/10 mt-24"
    >      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={IMG.brandLogo}
                alt="OGO Notary owl emblem"
                className="h-16 w-auto"
              />
              <div>
                <div className="font-serif-display text-xl text-white">
                  OGO Notary & Signing Services
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">
                  An OldGuyOutside, LLC Company
                </div>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md">
              OGO Notary &amp; Signing Services (An OldGuyOutside LLC Company).{" "}
              <span className="text-slate-200 font-semibold">
                Built on Experience + Grounded in Faith.
              </span>{" "}
              Providing elite Mobile Notary, Remote Online Notarization (RON),
              Ordained Ministerial Wedding Officiating, and compassionate Life
              Transition document support across Brevard County, Florida.
            </p>
            <div className="mt-6 flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.22em]">
              <ShieldCheck className="w-4 h-4" />
              Bonded · Insured · NNA Trained
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow mb-4">Navigate</div>
            <ul className="space-y-2 text-sm">
              {ALL_NAV.map((item) => {
                // Footer label override — spec calls for "Wedding & Life Services"
                const label =
                  item.to === "/wedding-services"
                    ? "Wedding & Life Services"
                    : item.label;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      data-testid={`footer-link-${item.testid}`}
                      className="text-slate-300 hover:text-[#D4AF37] transition-colors"
                    >
                      {label}
                    </Link>
                  </li>
                );
              })}
              <li>
                <Link
                  to="/vendor"
                  data-testid="footer-vendor-link"
                  className="text-[#D4AF37] hover:text-[#FDE047] transition-colors font-semibold"
                >
                  For Vendors →
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="eyebrow mb-4">Contact</div>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>
                Phone:{" "}
                <a
                  href="tel:+18555054722"
                  className="text-white hover:text-[#D4AF37]"
                >
                  (855) 505-4722
                </a>
              </li>
              <li>
                Email:{" "}
                <a
                  href="mailto:Greg@ogonotary.com"
                  className="text-white hover:text-[#D4AF37]"
                >
                  Greg@ogonotary.com
                </a>
              </li>
              <li>
                Service Area:{" "}
                <span className="text-white">
                  Brevard County, Florida · National / International
                </span>
              </li>
              <li>
                Address:{" "}
                <span className="text-white">
                  870 N. Miramar Ave. Unit #920<br />
                  Indialantic, FL 32903
                </span>
              </li>
              <li>
                Hours:{" "}
                <span className="text-white">
                  Mon–Sun · By Appointment · Nights & Weekends
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="gold-rule my-10" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div
            data-testid={FOOTER.legal}
            className="text-xs text-slate-500 tracking-wide"
          >
            © 2026 OGONotary.com | An OldGuyOutside, LLC Company. All rights
            reserved.
          </div>
          <div className="flex items-center gap-6 text-xs uppercase tracking-[0.22em]">
            <BuyMeACoffeeButton
              variant="inline"
              label="Buy Me a Coffee"
              testid="footer-bmc-btn"
            />
            <FooterAuthLink />
            <Link
              to="/privacy"
              data-testid={FOOTER.privacy}
              className="text-slate-400 hover:text-[#D4AF37]"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              data-testid={FOOTER.terms}
              className="text-slate-400 hover:text-[#D4AF37]"
            >
              Terms & Disclaimers
            </Link>
            <Link
              to="/contact"
              className="text-slate-400 hover:text-[#D4AF37]"
            >
              Contact
            </Link>
          </div>
        </div>

        <div
          data-testid={FOOTER.disclaimer}
          className="mt-8 text-[11px] leading-relaxed text-slate-500 max-w-4xl space-y-3"
        >
          <p>
            <strong className="text-slate-300">Disclaimer:</strong> OGO Notary
            &amp; Signing Services is not a law firm, and our staff are not
            attorneys licensed to practice law in Florida. We do not provide
            legal advice, draft legal documents, or accept fees for legal
            services. Our role is limited to the notarial acts authorized by
            Chapter 117 of the Florida Statutes and to the ministerial acts
            authorized to us as an Ordained Minister.
          </p>
          <p>
            OGO Notary &amp; Signing Services is a Florida-commissioned Notary
            Public operating under OldGuyOutside, LLC. Notary fees comply with
            state-regulated maximums. Travel, convenience, and after-hours
            service fees are separate from statutory stamp fees. Remote Online
            Notarization (RON) is performed in accordance with Florida Statutes
            using approved audio-video and identity-verification technology.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="App min-h-screen flex flex-col bg-[#142339] text-white">
      <Header />
      <AnnouncementBanner />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function AnnouncementBanner() {
  return (
    <section
      data-testid="ops-announcement-banner"
      role="region"
      aria-label="Operations announcement"
      className="w-full bg-[#D4AF37] text-[#0B1120] border-b-2 border-[#B8941F] shadow-[0_2px_0_0_rgba(0,0,0,0.15)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 sm:py-6">
        <div className="grid lg:grid-cols-12 gap-5 lg:gap-8 items-stretch">
          {/* LEFT — Operations Update text */}
          <div className="lg:col-span-7 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#0B1120] text-[#D4AF37]"
              >
                <Megaphone className="w-4 h-4" />
              </span>
              <span className="text-[11px] sm:text-xs uppercase tracking-[0.28em] font-extrabold whitespace-nowrap">
                📢 Operations Update
              </span>
            </div>
            <p className="text-sm sm:text-[15px] leading-relaxed font-medium">
              <span className="font-bold">
                Dr. Byers is currently completing a high-level International
                Operations Assignment through late 2026.
              </span>{" "}
              OGO Notary &amp; Signing Services is actively booking{" "}
              <span className="font-bold">Remote Online Notarizations (RON)</span>{" "}
              and scheduling{" "}
              <span className="font-bold">priority local mobile asset deployment</span>{" "}
              for Brevard County starting{" "}
              <span className="font-bold">January 2027</span>. Secure your
              preferred vendor slot at right. If you would like to discuss how
              we can help you or your company achieve secure, accurate notarial
              services, please reach out so we can begin a dialog on how we
              can be &quot;Of Service.&quot;
            </p>
          </div>

          {/* RIGHT — Founder credibility card + CTAs */}
          <div
            data-testid="ops-banner-founder-card"
            className="lg:col-span-5"
          >
            <div className="bg-[#0B1120] text-white p-4 sm:p-5 border-2 border-[#0B1120] h-full flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <Link
                  to="/about"
                  data-testid="ops-banner-founder-thumb"
                  aria-label="About the founder, Dr. Gregory Byers"
                  className="shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border-2 border-[#D4AF37] hover:border-[#FDE047] transition-colors"
                >
                  <img
                    src={IMG.headshotGreg}
                    alt="Dr. Gregory Byers, Founder"
                    loading="lazy"
                    className="w-full h-full object-cover object-top"
                  />
                </Link>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                    Speak directly to the Founder
                  </div>
                  <div className="font-serif-display text-lg sm:text-xl text-white leading-tight mt-1">
                    Dr. Gregory Byers
                  </div>
                  <div className="text-[11px] sm:text-xs text-slate-300 leading-snug mt-1">
                    45 years — DoD · DLA · U.S. State Dept.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-auto">
                <Link
                  to="/vendor"
                  data-testid="ops-banner-vendor-cta"
                  className="inline-flex items-center justify-center gap-1.5 bg-[#D4AF37] text-[#0B1120] hover:bg-[#FDE047] px-3 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors whitespace-nowrap"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Secure Vendor Slot
                </Link>
                <a
                  href="mailto:Greg@ogonotary.com?subject=Of%20Service%20-%20OGONotary%20Inquiry"
                  data-testid="ops-banner-email-cta"
                  className="inline-flex items-center justify-center gap-1.5 border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0B1120] px-3 py-2.5 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors whitespace-nowrap"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Email Greg
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
